import {
  ModelScene,
  DeleteScene,
  UpdateScene,
  EntityScene,
  CreateScene,
  useEntity,
  EntityFormActions,
  withHasPerm,
  IndexScene,
} from 'components';
import { Form } from './Form';
import { Nav } from './Nav';
import { all, useHasPerms } from 'core/permissions';
import { actions, columns } from 'modules/invoicing/expenses';
import permissions from 'modules/invoicing/expenses/permissions';
import { History, StateFilters } from 'modules/invoicing/common';
import { SynchronizationConflictsAlert } from 'modules/horizon/SynchronizationConflictsAlert';
import { route as conflictsRoute } from 'modules/horizon/Conflicts';
import { AuthState } from 'services';
import { ReactElement } from 'react';
import { Expense } from 'modules/invoicing/expenses/types';
import { CompanyFilterChip } from 'modules/core/company/components';
import { ExpenseLinker } from 'modules/linker';
import { InvoiceTypeFilterChip } from 'modules/invoicing/InvoiceTypeFilterChip';
import { ListExport } from 'components/ListActions';
import { Applicator } from 'modules/applicator/Applicator';
import AccountingSyncButton from 'modules/invoicing/common/AccountingSyncButton';
import { useAgencyVATRate } from 'modules/core/vat';
import { InvoiceStateFilterChip } from 'modules/invoicing/InvoiceStateFilterChip';
import { ListTotalsFooter } from 'modules/invoicing/common/ListTotalsFooter';
import { SearchFilterInput } from 'components/filters/SearchFilterInput';
import { SelectFilterChip } from 'components/filters/SelectFilterChip';
import { DateFilterChip } from 'components/filters/DateFilterChip';

const ProtectedExpenseLinker = withHasPerm(ExpenseLinker, permissions.link);
const ProtectedApplicator = withHasPerm(Applicator, permissions.change);

const model = {
  endpoint: 'invoicing/expense/',
  title: 'Expense',
  pluralTitle: 'Expenses',
  entityTitle: (e: Expense) => e.number,
  permissions,
};

function Create(): ReactElement {
  const vat_rate = useAgencyVATRate();
  const initialValues = {
    // display_number: string;
    state: 'DRAFT' as const,

    checkout_date: null,
    payment_date: null,
    comment: '',
    type: 'INVOICE' as const,
    alt_currency: null,
    alt_amount: null,
    alt_total: null,
    vat_rate,
    // sub_total: string;
    vat_amount: null,
    total: null,
    attachments: [],
    clients: [],
    finance_period: null,
  };

  return (
    <CreateScene initialValues={initialValues}>
      <Form bonus={false} />
    </CreateScene>
  );
}

function InvoiceUpdateScene(): ReactElement {
  const { item } = useEntity<Expense>();
  const isDraft = item.state === 'DRAFT';
  const [canChange] = useHasPerms(permissions.change);

  const disabled = !canChange || !isDraft;
  return (
    <UpdateScene
      heading={null}
      actions={<EntityFormActions actions={actions} ml="auto" />}
      disabled={disabled}
      perm={permissions.view}
    >
      <Form update lockDetails={!item.can_edit_details} bonus={item.source_cvb !== null} />
    </UpdateScene>
  );
}

const sourceOptions = [
  { value: 'CAMPAIGN', label: 'Campaign' },
  { value: 'CVB', label: 'CVB' },
];

const canManuallyCreateExpense = (s: AuthState): boolean => {
  const accounting = s.scope?.accounting_integration;
  return !accounting || accounting.supports_manual_create_expense;
};

export const route = {
  path: 'expense',
  element: <ModelScene {...model} />,
  children: [
    {
      index: true,
      element: (
        <IndexScene
          footer={<ListTotalsFooter />}
          canCreate={all(canManuallyCreateExpense, permissions.add)}
          columns={columns}
          before={<SynchronizationConflictsAlert />}
          toolbar={
            <>
              <AccountingSyncButton />
              <ListExport permission={permissions.export} />
            </>
          }
          storageKey="invoices_expense"
          filterFields={
            <>
              <SearchFilterInput />
              <SelectFilterChip name="source" options={sourceOptions} />
              <InvoiceTypeFilterChip />
              <CompanyFilterChip name="partner" label="Provider" />
              <CompanyFilterChip name="client" />
              <InvoiceStateFilterChip />
              <DateFilterChip name="date_from" />
              <DateFilterChip name="date_to" />
              <StateFilters />
            </>
          }
        />
      ),
    },
    conflictsRoute,
    {
      path: 'create',
      element: <Create />,
    },
    {
      path: ':id/',
      element: (
        <EntityScene>
          <Nav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: <InvoiceUpdateScene />,
        },
        {
          path: 'link',
          element: <ProtectedExpenseLinker />,
        },
        {
          path: 'application',
          element: <ProtectedApplicator />,
        },
        {
          path: 'delete',
          element: <DeleteScene />,
        },
        {
          path: 'history',
          element: <History />,
        },
      ],
    },
  ],
};
